import { useCallback, useContext } from 'react';
import { ToastContext, type ToastOptions } from '../contexts/ToastContext';

/**
 * Custom hook to display toast notifications from any component.
 * Toasts are rendered using the @yoast/ui-library Toast component via ToastContainer.
 *
 * The returned function is memoized and stable across renders, making it safe
 * to include in useEffect dependency arrays.
 *
 * @returns A memoized function to display toast messages
 *
 * @example
 * ```tsx
 * const toast = useToast();
 *
 * // Basic usage
 * toast('Operation successful!');
 *
 * // With options
 * toast('Error occurred', { variant: 'error', duration: 3000 });
 *
 * // Safe to use in useEffect dependency arrays
 * useEffect(() => {
 *   if (isError) {
 *     toast('Failed to load data', { variant: 'error' });
 *   }
 * }, [isError, toast]); // toast is stable and won't cause infinite loops
 * ```
 */
export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const { addToast } = context;

  // Memoize the toast function so it's stable across renders
  // addToast is already memoized in ToastProvider, so this callback
  // will only recreate when addToast changes (which is rare/never in practice)
  return useCallback(
    (message: string, options?: ToastOptions) => {
      addToast(message, options);
    },
    [addToast],
  );
};
